/* /src/mitras/regions.js
 *
 * Bharat-first regional taxonomy for the Mitra ecosystem.
 *
 * Two-level hierarchy: STATE → DISTRICT. City/Region (level 3) is kept
 * free-text for now since Indian district→tehsil mappings vary wildly
 * and the marketplace value is captured by State+District granularity.
 *
 * Coverage: every Indian state + UT, with districts for the launch
 * priorities (UP / Bihar / MP / Rajasthan / Maharashtra / Karnataka /
 * Tamil Nadu / Telangana / Gujarat / Haryana / Punjab / WB / Delhi NCR /
 * Andhra Pradesh / Kerala / Odisha / Jharkhand / Chhattisgarh / Assam).
 * Other states ship with a smaller capital-city seed and can be
 * expanded as we onboard Mitras there. Source: GoI district list as of
 * 2024 (community-maintained subset; expand via PR or admin import).
 *
 * Schema notes:
 *   • state_code matches ISO 3166-2:IN where applicable (e.g. "IN-UP").
 *   • district slugs are kebab-case and stable for SEO routes like
 *     `/mitras/design/lucknow`.
 *
 * Helpers:
 *   • listStates()   → [{code, name, name_hi}]
 *   • listDistricts(state_code) → [{slug, name}]
 *   • findState(code), findDistrict(state_code, slug)
 */

export const STATES = [
  { code: "IN-UP", name: "Uttar Pradesh", name_hi: "उत्तर प्रदेश", districts: [
    "Lucknow","Kanpur Nagar","Prayagraj","Varanasi","Agra","Meerut","Ghaziabad",
    "Noida (Gautam Buddh Nagar)","Bareilly","Aligarh","Moradabad","Saharanpur","Jhansi",
    "Gorakhpur","Faizabad (Ayodhya)","Mathura","Firozabad","Muzaffarnagar","Rampur","Sitapur",
    "Hardoi","Unnao","Raebareli","Sultanpur","Etawah","Pratapgarh","Mirzapur","Ballia","Azamgarh",
    "Deoria","Jaunpur","Bahraich","Gonda","Basti","Mainpuri","Etah","Hathras","Banda","Fatehpur",
    "Lakhimpur Kheri","Pilibhit","Shahjahanpur","Budaun","Bijnor","Amroha","Sambhal","Hapur",
    "Bulandshahr","Auraiya","Kanpur Dehat","Farrukhabad","Kannauj","Kushinagar","Maharajganj",
    "Sant Kabir Nagar","Siddharthnagar","Shrawasti","Balrampur","Ambedkar Nagar","Amethi","Barabanki",
    "Bhadohi","Chandauli","Chitrakoot","Ghazipur","Hamirpur","Jalaun","Kasganj","Lalitpur","Mahoba",
    "Mau","Sant Ravidas Nagar","Shamli","Sonbhadra"
  ]},
  { code: "IN-BR", name: "Bihar", name_hi: "बिहार", districts: [
    "Patna","Gaya","Bhagalpur","Muzaffarpur","Darbhanga","Purnia","Munger","Saharsa","Begusarai",
    "Hajipur (Vaishali)","Chhapra (Saran)","Siwan","Gopalganj","Madhubani","Samastipur","Sitamarhi",
    "Sheohar","Madhepura","Supaul","Araria","Kishanganj","Katihar","Banka","Jamui","Khagaria","Lakhisarai",
    "Sheikhpura","Nawada","Nalanda","Jehanabad","Arwal","Aurangabad","Rohtas","Kaimur","Buxar","Bhojpur",
    "Bettiah (West Champaran)","Motihari (East Champaran)"
  ]},
  { code: "IN-MP", name: "Madhya Pradesh", name_hi: "मध्य प्रदेश", districts: [
    "Bhopal","Indore","Gwalior","Jabalpur","Ujjain","Sagar","Dewas","Satna","Ratlam","Rewa","Singrauli",
    "Murwara (Katni)","Burhanpur","Khandwa","Chhindwara","Vidisha","Damoh","Mandsaur","Khargone","Neemuch",
    "Pithampur (Dhar)","Hoshangabad (Narmadapuram)","Itarsi","Sehore","Morena","Bhind","Shivpuri","Tikamgarh",
    "Niwari","Ashoknagar","Panna","Chhatarpur","Datia","Guna","Rajgarh","Shajapur","Agar Malwa","Alirajpur",
    "Anuppur","Balaghat","Barwani","Betul","Dhar","Dindori","Harda","Jhabua","Mandla","Mauganj","Narsinghpur",
    "Pandhurna","Raisen","Sheopur","Sidhi","Seoni","Shahdol","Umaria"
  ]},
  { code: "IN-RJ", name: "Rajasthan", name_hi: "राजस्थान", districts: [
    "Jaipur","Jodhpur","Udaipur","Kota","Ajmer","Bikaner","Bharatpur","Alwar","Sikar","Pali","Sri Ganganagar",
    "Hanumangarh","Tonk","Bundi","Chittorgarh","Banswara","Dungarpur","Pratapgarh","Rajsamand","Bhilwara",
    "Nagaur","Jhunjhunu","Churu","Barmer","Jaisalmer","Jalore","Sirohi","Sawai Madhopur","Karauli","Dholpur",
    "Dausa","Baran"
  ]},
  { code: "IN-MH", name: "Maharashtra", name_hi: "महाराष्ट्र", districts: [
    "Mumbai City","Mumbai Suburban","Thane","Palghar","Raigad","Pune","Pimpri-Chinchwad","Nashik","Aurangabad (Chh. Sambhajinagar)",
    "Nagpur","Solapur","Kolhapur","Sangli","Satara","Jalgaon","Ahmednagar","Latur","Amravati","Akola","Nanded",
    "Beed","Buldhana","Chandrapur","Dhule","Gadchiroli","Gondia","Hingoli","Jalna","Nandurbar","Parbhani","Ratnagiri",
    "Sindhudurg","Washim","Wardha","Yavatmal","Osmanabad (Dharashiv)"
  ]},
  { code: "IN-KA", name: "Karnataka", name_hi: "कर्नाटक", districts: [
    "Bengaluru Urban","Bengaluru Rural","Mysuru","Mangaluru (Dakshina Kannada)","Hubballi-Dharwad","Belagavi","Ballari",
    "Tumakuru","Davanagere","Shivamogga","Vijayapura","Kalaburagi","Raichur","Udupi","Hassan","Kolar","Chitradurga",
    "Mandya","Chikkamagaluru","Chamarajanagar","Kodagu","Yadgir","Koppal","Gadag","Haveri","Uttara Kannada","Bidar","Bagalkot"
  ]},
  { code: "IN-TN", name: "Tamil Nadu", name_hi: "तमिलनाडु", districts: [
    "Chennai","Coimbatore","Madurai","Tiruchirappalli","Salem","Tirunelveli","Erode","Vellore","Thoothukudi","Dindigul",
    "Kanchipuram","Tiruvallur","Krishnagiri","Cuddalore","Karur","Namakkal","Pudukkottai","Ramanathapuram","Sivagangai",
    "Tenkasi","Thanjavur","Nagapattinam","Nilgiris","Theni","Virudhunagar","Ranipet","Chengalpattu","Tirupathur","Mayiladuthurai",
    "Ariyalur","Tiruvarur","Kallakurichi","Kanyakumari","Perambalur","Tiruvannamalai","Villupuram","Dharmapuri"
  ]},
  { code: "IN-TG", name: "Telangana", name_hi: "तेलंगाना", districts: [
    "Hyderabad","Rangareddy","Medchal-Malkajgiri","Sangareddy","Warangal","Karimnagar","Khammam","Nizamabad","Adilabad",
    "Nalgonda","Mahbubnagar","Suryapet","Jagtial","Mancherial","Nirmal","Peddapalli","Jayashankar Bhupalpally","Mulugu",
    "Bhadradri Kothagudem","Jangaon","Hanumakonda","Mahabubabad","Yadadri Bhuvanagiri","Vikarabad","Wanaparthy","Nagarkurnool",
    "Jogulamba Gadwal","Narayanpet","Kamareddy","Rajanna Sircilla","Siddipet"
  ]},
  { code: "IN-GJ", name: "Gujarat", name_hi: "गुजरात", districts: [
    "Ahmedabad","Surat","Vadodara","Rajkot","Bhavnagar","Jamnagar","Junagadh","Gandhinagar","Anand","Bharuch","Mehsana",
    "Surendranagar","Patan","Kheda","Kutch","Valsad","Navsari","Porbandar","Amreli","Banaskantha","Sabarkantha","Aravalli",
    "Mahisagar","Panchmahal","Dahod","Tapi","Dang","Devbhumi Dwarka","Gir Somnath","Botad","Morbi","Chhota Udaipur"
  ]},
  { code: "IN-HR", name: "Haryana", name_hi: "हरियाणा", districts: [
    "Gurugram","Faridabad","Rohtak","Sonipat","Karnal","Panipat","Hisar","Sirsa","Ambala","Yamunanagar","Kurukshetra",
    "Kaithal","Jind","Bhiwani","Charkhi Dadri","Mahendragarh","Rewari","Jhajjar","Palwal","Nuh (Mewat)","Panchkula","Fatehabad"
  ]},
  { code: "IN-PB", name: "Punjab", name_hi: "पंजाब", districts: [
    "Ludhiana","Amritsar","Jalandhar","Patiala","Bathinda","Mohali (SAS Nagar)","Pathankot","Hoshiarpur","Sangrur","Rupnagar",
    "Mansa","Ferozepur","Faridkot","Moga","Muktsar","Barnala","Fazilka","Tarn Taran","Gurdaspur","Kapurthala","Fatehgarh Sahib",
    "Nawanshahr (SBS Nagar)","Malerkotla"
  ]},
  { code: "IN-WB", name: "West Bengal", name_hi: "पश्चिम बंगाल", districts: [
    "Kolkata","Howrah","Hooghly","North 24 Parganas","South 24 Parganas","Nadia","East Midnapore","West Midnapore","Burdwan East",
    "Burdwan West","Birbhum","Bankura","Purulia","Darjeeling","Jalpaiguri","Cooch Behar","Alipurduar","Murshidabad","Malda",
    "North Dinajpur","South Dinajpur","Kalimpong","Jhargram"
  ]},
  { code: "IN-DL", name: "Delhi", name_hi: "दिल्ली", districts: [
    "New Delhi","Central Delhi","North Delhi","South Delhi","East Delhi","West Delhi","North West Delhi","North East Delhi",
    "South West Delhi","South East Delhi","Shahdara"
  ]},
  { code: "IN-AP", name: "Andhra Pradesh", name_hi: "आंध्र प्रदेश", districts: [
    "Visakhapatnam","Vijayawada (Krishna)","Guntur","Tirupati","Nellore","Kurnool","Kadapa","Anantapur","Sri Sathya Sai",
    "Chittoor","Annamayya","East Godavari","West Godavari","Eluru","Konaseema","Kakinada","Bapatla","Palnadu","Prakasam",
    "Srikakulam","Vizianagaram","Manyam","Alluri Sitharama Raju","Anakapalli","NTR"
  ]},
  { code: "IN-KL", name: "Kerala", name_hi: "केरल", districts: [
    "Thiruvananthapuram","Kochi (Ernakulam)","Kozhikode","Thrissur","Kollam","Kannur","Alappuzha","Kottayam","Palakkad",
    "Malappuram","Pathanamthitta","Idukki","Kasaragod","Wayanad"
  ]},
  { code: "IN-OR", name: "Odisha", name_hi: "ओडिशा", districts: [
    "Bhubaneswar (Khordha)","Cuttack","Rourkela (Sundargarh)","Berhampur (Ganjam)","Sambalpur","Puri","Balasore","Bhadrak",
    "Mayurbhanj","Kendrapara","Jagatsinghpur","Jajpur","Dhenkanal","Angul","Nayagarh","Boudh","Subarnapur","Bargarh","Jharsuguda",
    "Bolangir","Kalahandi","Nabarangpur","Koraput","Rayagada","Malkangiri","Nuapada","Kandhamal","Gajapati","Keonjhar","Deogarh"
  ]},
  { code: "IN-JH", name: "Jharkhand", name_hi: "झारखंड", districts: [
    "Ranchi","Jamshedpur (East Singhbhum)","Dhanbad","Bokaro","Hazaribagh","Deoghar","Giridih","Palamu","Garhwa","Latehar",
    "Lohardaga","Gumla","Khunti","West Singhbhum","Saraikela Kharsawan","Simdega","Chatra","Koderma","Ramgarh","Pakur",
    "Dumka","Jamtara","Sahibganj","Godda"
  ]},
  { code: "IN-CT", name: "Chhattisgarh", name_hi: "छत्तीसगढ़", districts: [
    "Raipur","Bhilai (Durg)","Bilaspur","Korba","Raigarh","Rajnandgaon","Jagdalpur (Bastar)","Ambikapur (Surguja)","Janjgir-Champa",
    "Mahasamund","Dhamtari","Kawardha (Kabirdham)","Bemetara","Balod","Baloda Bazar","Gariaband","Mungeli","Sukma","Dantewada",
    "Bijapur","Kondagaon","Narayanpur","Kanker","Jashpur","Surajpur","Balrampur (CG)","Koriya","Manendragarh","Sakti","Sarangarh-Bilaigarh","Khairagarh"
  ]},
  { code: "IN-AS", name: "Assam", name_hi: "असम", districts: [
    "Guwahati (Kamrup Metropolitan)","Kamrup","Dibrugarh","Jorhat","Tinsukia","Nagaon","Sivasagar","Tezpur (Sonitpur)","Silchar (Cachar)",
    "Karimganj","Hailakandi","Barpeta","Bongaigaon","Dhubri","Goalpara","Kokrajhar","Morigaon","Lakhimpur","Dhemaji","Nalbari",
    "Darrang","Udalguri","Baksa","Chirang","South Salmara-Mankachar","Karbi Anglong","Dima Hasao","Charaideo","Majuli","Hojai",
    "West Karbi Anglong","Biswanath","Bajali"
  ]},
  // Smaller / remaining states & UTs — capital-city seed
  { code: "IN-UK", name: "Uttarakhand", name_hi: "उत्तराखंड", districts: ["Dehradun","Haridwar","Nainital","Udham Singh Nagar","Pauri Garhwal","Tehri Garhwal","Almora","Pithoragarh","Chamoli","Champawat","Rudraprayag","Uttarkashi","Bageshwar"] },
  { code: "IN-HP", name: "Himachal Pradesh", name_hi: "हिमाचल प्रदेश", districts: ["Shimla","Solan","Mandi","Kangra","Una","Bilaspur","Hamirpur","Chamba","Kullu","Sirmaur","Kinnaur","Lahaul and Spiti"] },
  { code: "IN-JK", name: "Jammu and Kashmir", name_hi: "जम्मू और कश्मीर", districts: ["Srinagar","Jammu","Baramulla","Anantnag","Pulwama","Kupwara","Budgam","Kathua","Udhampur","Doda","Kishtwar","Ramban","Reasi","Samba","Poonch","Rajouri","Kulgam","Shopian","Ganderbal","Bandipora"] },
  { code: "IN-LA", name: "Ladakh", name_hi: "लद्दाख", districts: ["Leh","Kargil"] },
  { code: "IN-GA", name: "Goa", name_hi: "गोवा", districts: ["North Goa","South Goa"] },
  { code: "IN-SK", name: "Sikkim", name_hi: "सिक्किम", districts: ["Gangtok","Pakyong","Mangan","Soreng","Namchi","Gyalshing"] },
  { code: "IN-MN", name: "Manipur", name_hi: "मणिपुर", districts: ["Imphal East","Imphal West","Bishnupur","Thoubal","Churachandpur","Senapati","Tamenglong","Ukhrul","Chandel","Jiribam","Kakching","Kangpokpi","Kamjong","Noney","Pherzawl","Tengnoupal"] },
  { code: "IN-MZ", name: "Mizoram", name_hi: "मिज़ोरम", districts: ["Aizawl","Lunglei","Champhai","Mamit","Kolasib","Serchhip","Lawngtlai","Saiha","Hnahthial","Khawzawl","Saitual"] },
  { code: "IN-ML", name: "Meghalaya", name_hi: "मेघालय", districts: ["East Khasi Hills","West Khasi Hills","South West Khasi Hills","Ri Bhoi","Eastern West Khasi Hills","West Jaintia Hills","East Jaintia Hills","West Garo Hills","East Garo Hills","South Garo Hills","North Garo Hills","South West Garo Hills"] },
  { code: "IN-TR", name: "Tripura", name_hi: "त्रिपुरा", districts: ["West Tripura","South Tripura","North Tripura","Dhalai","Khowai","Sepahijala","Gomati","Unakoti"] },
  { code: "IN-NL", name: "Nagaland", name_hi: "नागालैंड", districts: ["Kohima","Dimapur","Mokokchung","Mon","Tuensang","Wokha","Zunheboto","Phek","Kiphire","Longleng","Peren","Noklak","Chümoukedima","Niuland","Tseminyu","Shamator"] },
  { code: "IN-AR", name: "Arunachal Pradesh", name_hi: "अरुणाचल प्रदेश", districts: ["Itanagar (Papum Pare)","East Siang","West Siang","Upper Siang","Lower Siang","Siang","Tawang","West Kameng","East Kameng","Pakke Kessang","Kurung Kumey","Kra Daadi","Lower Subansiri","Upper Subansiri","Kamle","Lohit","Anjaw","Changlang","Tirap","Longding","Namsai","Dibang Valley","Lower Dibang Valley","Leparada","Shi Yomi"] },
  { code: "IN-CH", name: "Chandigarh", name_hi: "चंडीगढ़", districts: ["Chandigarh"] },
  { code: "IN-DN", name: "Dadra & Nagar Haveli and Daman & Diu", name_hi: "दादरा और नगर हवेली एवं दमन और दीव", districts: ["Daman","Diu","Dadra and Nagar Haveli"] },
  { code: "IN-LD", name: "Lakshadweep", name_hi: "लक्षद्वीप", districts: ["Lakshadweep"] },
  { code: "IN-PY", name: "Puducherry", name_hi: "पुडुचेरी", districts: ["Puducherry","Karaikal","Mahe","Yanam"] },
  { code: "IN-AN", name: "Andaman & Nicobar Islands", name_hi: "अंडमान और निकोबार द्वीप समूह", districts: ["South Andaman","North & Middle Andaman","Nicobar"] },
];

// Slugify a district name into the kebab-case form used in SEO URLs.
// "Mumbai Suburban" → "mumbai-suburban", "Bengaluru Urban" → "bengaluru-urban".
const slugify = (s) =>
  String(s)
    .toLowerCase()
    .replace(/[()]/g, " ")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

// Public helpers — keep the lookup surface small so consumers (forms,
// admin filters, future SEO routes) don't need to introspect STATES.
export const listStates = () =>
  STATES.map((s) => ({ code: s.code, name: s.name, name_hi: s.name_hi }));

export const listDistricts = (stateCode) => {
  const state = STATES.find((s) => s.code === stateCode);
  if (!state) return [];
  return state.districts.map((name) => ({ name, slug: slugify(name) }));
};

export const findState = (codeOrName) =>
  STATES.find(
    (s) => s.code === codeOrName || s.name.toLowerCase() === String(codeOrName || "").toLowerCase()
  ) || null;

export const findDistrict = (stateCode, slugOrName) => {
  const state = findState(stateCode);
  if (!state) return null;
  const slug = slugify(slugOrName || "");
  return (
    state.districts
      .map((name) => ({ name, slug: slugify(name) }))
      .find((d) => d.slug === slug || d.name.toLowerCase() === slug.replace(/-/g, " ")) || null
  );
};

export const districtSlug = slugify;
